import { LenderConfiguration } from '@shared/model/lender-config.model';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingUrl } from '@shared/constant/route-const';
import { Params, TargetType } from '@shared/constant/target-flow-const';
import { StatefulService } from '@app/service/stateful.service';
import { HostPrefillService } from '@workflow/ecp-workflow/dynamic-app/service/host-prefill.service';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { CUDLConnect } from '@workflow/ecp-workflow/dynamic-app/component/building-block/app-prefill/app-prefill.type';
import { HttpErrorResponse } from '@angular/common/http';
import { LoanType } from '@shared/enum';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { ConsumerLogin } from 'src/user/model/consumer-login';
import { OIDLoginWidgetExportData } from '@cudirect-widgets/ea-login';
import { AppConfigService as ConfigService, GoogleTagManagerService, TokenService } from '@cudirect/shared-ui-library';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { StorageService } from '@workflow/service/storage/storage.service';
import { UserSessionService } from '@shared/service/user-session.service';
// eslint-disable-next-line max-len
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { XApiKeyService } from '@shared/service/x-api-key.service';

interface HandleReceivedDataForLoginModeResponse {
  phoneNumber?: string;
  email?: string;
}

interface GetLoginDataProps {
  email: string;
  phoneNumber: string;
  accessToken: string;
  refreshToken: string;
  ssoLogin?: boolean;
  idToken?: string;
  expiresIn?: string;
  authCode?: string;
  extToken?: string;
  discoveryURL?: string;
  loginMode: 'login' | 'heavyReg' | 'verify';
}

interface GetLoginDataResponse {
  oldSession: string;
  user: ConsumerLogin;
  prevSession: string;
  comingFromHeavyReg: string;
}

/**
 *
 * Redirect UI Binding
 */
@Component({
  selector: 'cu-redirect',
  templateUrl: './redirect.component.html'
})
export class RedirectComponent implements OnInit {
  /** BS Model Ref */
  public lenderData: LenderConfiguration;
  private target = '';
  private flowType = '';

  constructor(
    private readonly router: Router,
    public readonly activeRoute: ActivatedRoute,
    private readonly statefulService: StatefulService,
    private readonly hostPrefillService: HostPrefillService,
    private readonly tokenService: TokenService,
    private readonly userSession: UserSessionService,
    private readonly pageNavigationStateService: WorkflowPageNavigationStateService,
    private readonly configService: ConfigService,
    private readonly xApiKeyService: XApiKeyService,
    public readonly storageService: StorageService,
    public readonly userService: UserService,
    public readonly l360Service: L360Service,
    private readonly bundleHelperService: BundleHelperService,
    private readonly gtm: GoogleTagManagerService
  ) {}

  public ngOnInit(): void {
    // Redirect based on target value : targetflow={{target}}
    this.activeRoute.queryParams.subscribe((params) => {
      this.target = params[Params.target]?.toLowerCase().substring(0, 3);
      const ola = this.target === TargetType.OLA;
      const protocol = ola ? 'ola' : 'api';
      this.flowType = ola ? 'OLA' : 'OAO';
      const isHostPrefillCompleted = this.statefulService.wrap(`${protocol}:isHostPrefillCompleted`).value as boolean;
      const ssisHostPrefillCompleted = this.statefulService.wrap('ss:isHostPrefillCompleted').value;
      this.saveOtuTokenToBundle(ola, params[Params.token]);
      if (!ssisHostPrefillCompleted && !isHostPrefillCompleted) {
        this.prefillData(this.target === TargetType.OLA, params[Params.token])
          .then(() => {
            this.navigateNext();
          })
          .catch(() => {
            this.navigateNext();
          });
      }
    });
  }

  private navigateNext() {
    if (this.target === TargetType.OLA) {
      // Persist the prefill params while removing some of the ones when navigating to OLA
      this.router.navigate([RoutingUrl.OLA.LoanType], {
        queryParams: {
          otutoken: null,
          target: null,
          frame: null
        },
        queryParamsHandling: 'merge'
      });
    }
    if (this.target === TargetType.OAO) {
      // Why do we need a fresh loan flag in session storage? What is this powering?
      this.statefulService.wrap('ss:oao-fresh-loan').value = true;
      // Persist the prefill params while removing some of the ones when navigating to OLA
      this.router.navigate([RoutingUrl.OAO.Membership], {
        queryParams: {
          otutoken: null,
          target: null,
          frame: null
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  // Why do we needthe otutoken if its one time use? Is it for debugging? Why does the bundle need this here?
  public saveOtuTokenToBundle(isOla: boolean, otutoken: string): void {
    const protocol = isOla ? 'ola' : 'api';
    this.statefulService.wrap(`${protocol}:hostPrefillToken`).value = otutoken;

    if (isOla) {
      this.statefulService.wrap('ola:olaApplicationInformation/membership/isMember').value = true;
    }
  }

  /**
   * Initializes the token service with configuration settings and sets up caching for user session tokens.
   *
   * @remarks
   * This method is used to initialize the token service with necessary configuration and API keys.
   * It also configures the caching mechanism for storing user session tokens in either `sessionStorage`
   * or another specified storage type.
   *
   * @returns A promise that resolves once the token service is successfully initialized and configured.
   */
  private async setupTokenInitialize(): Promise<void> {
    return this.tokenService
      .initialize(
        this.configService?.settings?.oid.apiBaseURL,
        this.xApiKeyService.createApiHeader(),
        this.configService?.settings?.oid.oidConfigName,
        true
      )
      .then(() => {
        this.tokenService.configureCache(this.configService?.settings?.arcDXTokenStorageType || 'sessionStorage', 'ecp-user-session');
      });
  }

  /**
   * Sets up the verify flow for the login process.
   * This function prepares the data object required for the subsequent authentication steps.
   *
   * @param options - An object containing the access and refresh tokens.
   * @param options.accessToken - The access token for authentication.
   * @param options.refreshToken - The refresh token for obtaining new access tokens.
   * @returns A Promise that resolves to an OIDLoginWidgetExportData object, containing
   *          login-related information including tokens and authentication status.
   */
  private async setUpVerifyFlow({
    accessToken,
    refreshToken
  }: {
    accessToken: string;
    refreshToken: string;
  }): Promise<OIDLoginWidgetExportData> {
    const data = {
      loginRedirect: false,
      provider: '',
      statusMsg: 'Authenticated',
      token: {
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_in: 0,
        id_token: null,
        token_type: null
      }
    } as OIDLoginWidgetExportData;

    return Promise.resolve(data);
  }

  /**
   * Handles the received data from the login widget.
   * This function acts as a gatekeeper for the login process, processing the data
   * retrieved from the loginService.getLoginData() based on the current login mode.
   *
   * @param data - The login widget export data containing authentication information.
   * @returns A promise that resolves with the processed OIDLoginWidgetExportData.
   */
  private async receivedDataHandler(data: OIDLoginWidgetExportData): Promise<OIDLoginWidgetExportData> {
    return Promise.resolve(data);
  }

  /**
   * Processes the received data from the login widget for the login mode.
   * This function extracts the access token from the input data, decodes it,
   * and returns the user's email and phone number.
   *
   * @param data - The data received from the login widget.
   * @returns A Promise that resolves to an object containing the user's phone number and email.
   * @throws Error if the token format is invalid.
   */
  private async handleReceivedDataForLoginMode(data: OIDLoginWidgetExportData): Promise<HandleReceivedDataForLoginModeResponse> {
    let accessToken: string;

    if (typeof data.token === 'string') {
      accessToken = data.token;
    } else if (data.token && 'access_token' in data.token) {
      accessToken = data.token.access_token;
    } else {
      throw new Error('Invalid token format');
    }

    const decodedToken = this.tokenService.decodeToken(accessToken);
    const { email, phonenumber } = decodedToken;

    return Promise.resolve({
      phoneNumber: phonenumber,
      email
    });
  }

  /**
   * Processes login data and prepares user information for authentication.
   *
   * @param options - The login data options.
   * @param options.email - The user's email address.
   * @param options.phoneNumber - The user's phone number.
   * @param options.accessToken - The access token for authentication.
   * @param options.refreshToken - The refresh token for obtaining new access tokens.
   * @param options.ssoLogin - Indicates if the login is via SSO.
   * @param options.loginMode - The mode of login ('login', 'heavyReg', or 'verify').
   * @returns A promise that resolves to an object containing login-related data.
   */
  private async getLoginData({
    email = '',
    phoneNumber = '',
    accessToken = '',
    refreshToken = '',
    ssoLogin = false,
    loginMode
  }: GetLoginDataProps): Promise<GetLoginDataResponse> {
    const user = {} as ConsumerLogin;
    // Widget Login Flow
    user.email = email;
    user.accessToken = accessToken;
    user.refreshToken = refreshToken;
    user.loginWithSSO = ssoLogin;
    user.cellPhone = phoneNumber;

    const isOlaFlow = this.target === TargetType.OLA ? true : false;

    const oldSession = this.statefulService.wrap(`${isOlaFlow ? 'ss:auth-ola' : 'ss:authorization'}`).valueString; // guid
    let prevSession = '';
    if (loginMode === 'verify') {
      prevSession = oldSession ?? null; // prevSession is same guid as oldSession
    }
    this.statefulService.wrap('ss:loginType').value = false;
    const comingFromHeavyReg = sessionStorage.getItem('arcDXHeavyReg'); // is null

    // before the function would call getLoginDataContinued(), but we are going to pivot away from that now
    return Promise.resolve({
      oldSession,
      user,
      prevSession,
      comingFromHeavyReg
    });
  }

  /**
   * Continues the login process after initial login data has been retrieved.
   * This method handles session management, user initialization, and prepares for the next steps in the login flow.
   *
   * @param options - An object containing the login continuation options.
   * @param options.oldSession - The session token from the previous session.
   * @returns A Promise that resolves when the login continuation process is complete.
   */
  private async getLoginDataContinued({ oldSession }: { oldSession: string }): Promise<void> {
    sessionStorage.removeItem('arcDXHeavyReg');
    if (this.userSession.canHookCurrentBundle && oldSession) {
      this.userService.user = new UserInfo();
      this.userService.user.token = oldSession;
    }
    this.userSession.canHookCurrentBundle = false;
    return Promise.resolve();
  }

  /**
   * Sets up the user information based on the provided login data.
   * This method initializes a new UserInfo object, populates it with the given user data,
   * sets the user in the userService, determines the flow type, starts the session timer,
   * and updates the authorization value in the stateful service.
   *
   * @param options - The options for setting up the user.
   * @param options.user - The ConsumerLogin object containing user login information.
   * @param options.oldSession - The session token from the previous session.
   * @param options.loginType - The type of login being performed (optional).
   * @param options.mfaVerifyMethod - The method used for multi-factor authentication verification.
   * @returns A Promise that resolves to an object containing the initialized UserInfo.
   */
  private async setUser({
    user,
    oldSession,
    loginType,
    mfaVerifyMethod
  }: {
    user: ConsumerLogin;
    oldSession: string;
    mfaVerifyMethod: 'Email_Code' | 'Phone';
    loginType?: string;
  }): Promise<{ userInfo: UserInfo }> {
    const userInfo = new UserInfo();
    userInfo.email = user.email;
    userInfo.cellPhone = user.cellPhone;
    userInfo.isCellPhoneUser = !!user.cellPhone;
    userInfo.mfaVerifyMethod = mfaVerifyMethod;
    userInfo.token = oldSession;
    userInfo.token_time = new Date();
    userInfo.confetti = [];
    userInfo.registeredWithSSO = user.loginWithSSO;
    userInfo.loginType = loginType;

    this.userService.user = userInfo;
    if (loginType?.toLowerCase() === LoanType.OLA.toLowerCase()) {
      this.userService.flowType = LoanType.OLA;
    } else if (loginType?.toLowerCase() === LoanType.OAO.toLowerCase()) {
      this.userService.flowType = LoanType.OAO;
    }

    this.userSession.startTimer();
    this.statefulService.wrap('ss:authorization').value = oldSession;

    return Promise.resolve({ userInfo });
  }

  /**
   * Performs post-login operations based on the user information and login mode.
   * This method handles specific actions for OAO (Online Account Opening) login types,
   * such as updating lender settings.
   *
   * @param options - The options for the afterLogin operation.
   * @param options.userInfo - The user information object containing details about the logged-in user.
   * @param options.loginMode - The mode of login, which can be 'login', 'heavyReg', or 'verify'.
   * @returns A Promise that resolves when all post-login operations are completed.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async afterLogin({ userInfo, loginMode }: { userInfo: UserInfo; loginMode: 'login' | 'heavyReg' | 'verify' }): Promise<void> {
    if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
      /** Updating OAO related settings which comes from L360 */
      this.l360Service.setOAOLenderSettings();
    }

    return Promise.resolve();
  }

  /**
   * Sets up the OAO (Online Account Opening) member information and initializes related services.
   *
   * This function is responsible for setting up the storage prefix for page navigation
   * and retrieving the page state for OAO members. It's designed to be used after
   * a user has been authenticated and their information is available.
   *
   * @param params - The parameters object.
   * @param params.userInfo - The user information object containing details about the authenticated user.
   *
   * @returns A promise that resolves when the OAO member setup is complete.
   */
  private async setOAOMember({ userInfo }: { userInfo: UserInfo }): Promise<void> {
    if (userInfo) {
      // we dont have extraData so not settting it here.
      // const userExtraData = user.extraData;
      // this.statefulService.wrap('api:extraData').value = userExtraData;
      this.pageNavigationStateService.setStoragePrefix('oao');
      this.pageNavigationStateService.retrievePageState();
      // if (userExtraData) {
      //   this.setNavbarStatus(userExtraData);
      // }
    }
    return Promise.resolve();
  }

  /**
   * Extracts the Multi-Factor Authentication (MFA) verification method from the provided CUDLConnect response.
   *
   * @param res - The CUDLConnect response object containing applicant information.
   * @returns An object with the following properties:
   *   - isValid: Indicates whether a valid MFA method was found.
   *   - method: The MFA method, either 'Email_Code' or 'Phone' (if valid).
   *   - value: The value associated with the MFA method (email or phone number).
   *   - phone: The applicant's phone number (if available).
   *   - email: The applicant's email address (if available).
   */
  private extractMfaVerifyMethod(res: CUDLConnect): {
    isValid: boolean;
    method?: 'Email_Code' | 'Phone';
    value?: string;
    phone?: string;
    email?: string;
  } {
    if (!res?.cudlAccountLink?.applicant || !Array.isArray(res.cudlAccountLink.applicant)) {
      return {
        isValid: false
      };
    }

    const applicants = res.cudlAccountLink.applicant;
    if (!applicants.length) {
      return {
        isValid: false
      };
    }

    const retval = {
      isValid: false
    };

    const applicant = applicants[0];
    if (applicant.phoneNumber && Array.isArray(applicant.phoneNumber)) {
      const mobilePhone = applicant.phoneNumber.find((phone) => phone.phoneNumberType === 'Mobile');
      if (mobilePhone?.number) {
        Object.assign(retval, {
          isValid: true,
          method: 'Phone',
          value: mobilePhone.number,
          phone: mobilePhone.number
        });
      }
    }

    if (applicant.emailAddress.length > 0) {
      Object.assign(retval, {
        email: applicant.emailAddress[0]
      });

      if (!retval.isValid) {
        Object.assign(retval, {
          isValid: true,
          method: 'Email_Code',
          value: applicant.emailAddress[0]
        });
      }
    }

    return retval;
  }

  /**
   * Method to get the prefill data by calling the API
   * @param isOla determine whether the flow is OLA or not
   * @param otuToken current token
   */
  public prefillData(isOla: boolean, otuToken: string): Promise<void> {
    this.hostPrefillService.flowId = isOla ? 'ola' : 'oao';

    const promise = isOla ? this.bundleHelperService.refreshOLABundleAsync() : this.bundleHelperService.refreshBundleAsync();
    return promise
      .then(() => {
        return this.hostPrefillService.getHbData(otuToken).then((res?: CUDLConnect) => {
          if (!res?.internalJwtToken?.access_token || !res?.internalJwtToken?.refresh_token) {
            let gaActionName = 'arcdx.';
            if (this.flowType === 'OLA') {
              gaActionName += 'loans.';
            } else {
              gaActionName += 'accounts.';
            }
            gaActionName += 'prefill-error';
            this.gtm.publishEvent(gaActionName, {
              Page: 'Homebanking Redirect',
              ApplicationType: this.flowType
            });
            return;
          }

          // Retrieve loginType from StatefulValue
          const loginType = (this.statefulService.wrap('api:loginType').value as string) || isOla ? 'ola' : 'oao';

          const {
            isValid: isValidMfa,
            method: mfaVerifyMethod,
            // value: mfaValue,
            phone: phoneValue,
            email: emailValue
          } = this.extractMfaVerifyMethod(res);

          if (!isValidMfa) {
            let gaActionName = 'arcdx.';
            if (this.flowType === 'OLA') {
              gaActionName += 'loans.';
            } else {
              gaActionName += 'accounts.';
            }
            gaActionName += 'prefill-error';
            this.gtm.publishEvent(gaActionName, {
              Page: 'Homebanking Redirect',
              ApplicationType: this.flowType
            });
            throw new Error('Missing or invalid MFA method');
          }

          const { access_token: accessToken, refresh_token: refreshToken } = res.internalJwtToken;
          // This line will hide the user dropdown until the user has passed personal info.
          this.userService.isExistingUser = res.isExistingUser;
          this.tokenService.updateToken({
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_in: 0,
            id_token: null,
            token_type: null
          });

          // duplicate logic from login component
          const loginMode = 'verify';
          return this.setupTokenInitialize()
            .then(() => {
              return this.setUpVerifyFlow({
                refreshToken,
                accessToken
              });
            })
            .then((result: OIDLoginWidgetExportData) => {
              return this.receivedDataHandler(result);
            })
            .then((result: OIDLoginWidgetExportData) => {
              return this.handleReceivedDataForLoginMode(result)
                .then((handled: HandleReceivedDataForLoginModeResponse) => {
                  return this.getLoginData({
                    loginMode,
                    email: mfaVerifyMethod === 'Email_Code' ? handled.email : emailValue,
                    phoneNumber: mfaVerifyMethod === 'Phone' ? handled.phoneNumber : phoneValue,
                    accessToken,
                    refreshToken,
                    ssoLogin: false
                  });
                })
                .then((loginData: GetLoginDataResponse) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { oldSession, user, comingFromHeavyReg } = loginData;
                  let ipromise;
                  // prevSession used to be used in getLoginDataContinued() when it called this.userService.login()
                  // isAllBackendInitialized() is false through the oao flow, compared to ola flow
                  if (oldSession && !comingFromHeavyReg && this.storageService.isAllBackendInitialized()) {
                    ipromise = this.getLoginDataContinued({ oldSession });
                  } else {
                    ipromise = this.storageService.initAllBackends().then(() => {
                      return this.getLoginDataContinued({ oldSession });
                    });
                  }
                  return ipromise
                    .then(() => {
                      let gaActionName = 'arcdx.';
                      if (this.flowType === 'OLA') {
                        gaActionName += 'loans.';
                      } else {
                        gaActionName += 'accounts.';
                      }
                      gaActionName += 'prefill-success';
                      this.gtm.publishEvent(gaActionName, {
                        Page: 'Homebanking Redirect',
                        ApplicationType: this.flowType
                      });
                      return this.setUser({ user, oldSession, loginType, mfaVerifyMethod });
                    })
                    .then(({ userInfo }: { userInfo: UserInfo }) => {
                      if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
                        return this.afterLogin({ userInfo, loginMode }).then(() => {
                          return this.setOAOMember({ userInfo });
                        });
                      }
                    });
                });
            })
            .catch((error: HttpErrorResponse) => {
              let gaActionName = 'arcdx.';
              if (this.flowType === 'OLA') {
                gaActionName += 'loans.';
              } else {
                gaActionName += 'accounts.';
              }
              gaActionName += 'prefill-error';
              this.gtm.publishEvent(gaActionName, {
                Page: 'Homebanking Redirect',
                ApplicationType: this.flowType
              });
              console.error('Error in prefillData', error);
              throw error;
            });
        });
      })
      .catch((err) => {
        let gaActionName = 'arcdx.';
        if (this.flowType === 'OLA') {
          gaActionName += 'loans.';
        } else {
          gaActionName += 'accounts.';
        }
        gaActionName += 'prefill-error';
        this.gtm.publishEvent(gaActionName, {
          Page: 'Homebanking Redirect',
          ApplicationType: this.flowType
        });
        console.error('Error in prefillData', err);
        throw err;
      });
  }
}
